import { Button, Card, CardContent } from '@/ui/design-system/components';
import { LifeCycleRow } from 'components/LifeCycleBadge';
import { POPULARITY_METADATA_BY_KEY } from 'utils/metrics';
import {
    TopHashtagsDrawerSection,
    TopInstagramHashtagsDrawerSection,
    TopKeywordsDrawerSection,
} from 'components/TopKeywordsSection';
import { getPathFromNode } from 'utils/helpers';
import styled, { ThemeContext } from 'styled-components';
import { useContext, useRef, useState } from 'react';
import CategoryDescriptionCard from 'components/CategoryDescriptionCard';
import DimensionIcon from 'components/DimensionIcon';
import DownloadPopularityTimeSeriesButton from 'components/Button/DownloadPopularityTimeSeriesButton';
import DrawerBand from 'components/DrawerBand';
import DrawerHeader from 'components/DrawerHeader';
import { buildExportFilename } from 'utils/formatCSV';
import ErrorBoundary from 'containers/ErrorBoundary';
import { FiExternalLink } from 'react-icons/fi';
import { DATA_SOURCES, INSTAGRAM_COUNTRIES } from '@/config/constants';
import { LinkTracked } from 'components/Link';
import PopularityTimeSeries from 'components/DataVis/Charts/PopularityTimeSeries';
import Stack from 'components/Stack';
import ToggleWatch from 'components/Button/ToggleWatch';
import dayjs from 'dayjs';
import { getParentIdByAncestorHint } from 'utils/hooks/useGetHierarchy';
import { getSubcategoryLabel } from 'utils/hierarchies';
import { useCurrentCountry, useEndDate } from 'utils/hooks';
import useTikTokWeeks from 'utils/hooks/useTikTokWeeks';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import MetricHeroCard from 'components/MetricHeroCard';

dayjs.extend(weekOfYear);

const CHART_HEIGHT = 287;

const DrawerStack = styled(Stack)`
    padding: 0 1rem;
`;

const Message = styled.div`
    color: ${({ theme }) => theme.gray_2};
    font-weight: 500;
    min-height: 4.125rem;
    display: flex;
    justify-content: center;
    align-items: center;
`;

function OverviewDrawer({
    paginateItem,
    data,
    timeSeriesTikTok,
    timeSeriesGoogle,
    timeSeriesInstagram,
    parentCategory,
    showAddToWatchlist = true,
}) {
    const parentHierarchies = parentCategory?.hierarchies;
    const theme = useContext(ThemeContext);
    const country = useCurrentCountry();
    const timeSeriesRef = useRef();
    const paginateType = paginateItem?.type;
    const paginateBy = paginateType === 'category' ? 'categories' : 'brands';
    const { name, hierarchies } = paginateItem || {};
    let category, brand;
    if (paginateItem?.type === 'category') {
        category = paginateItem.id;
        brand = null;
    } else if (paginateItem?.type === 'brand') {
        category = parentCategory?.id || data?.category;
        brand = paginateItem.id;
    }
    const { week, year, query: weeksQuery } = useTikTokWeeks();
    const popIndexDate = useEndDate('pop-index');
    const googleDate = useEndDate('google');
    const tiktokDate = useEndDate('tiktok');
    const instagramDate = useEndDate('instagram');
    const parentId = getParentIdByAncestorHint(
        hierarchies,
        parentHierarchies?.[0]?.[1],
    );
    const subcategory = getSubcategoryLabel(hierarchies, parentCategory);

    const timeSeriesName = `${name} (${country?.code || country?.name})`;
    const tsData = [];
    if (timeSeriesGoogle)
        tsData.push({
            id: 'google',
            name: `Google${country ? ` (${country.code || country.name})` : null}`,
            timeSeries: timeSeriesGoogle,
            color: theme.google_blue,
        });
    if (timeSeriesTikTok)
        tsData.push({
            id: 'tiktok',
            name: `TikTok${country ? ` (${country.code || country.name})` : null}`,
            timeSeries: timeSeriesTikTok,
            color: theme.tiktok_pink,
        });
    if (timeSeriesInstagram)
        tsData.push({
            id: 'instagram',
            name: `Instagram${country ? ` (${country.code || country.name})` : null}`,
            timeSeries: timeSeriesInstagram,
            color: theme.instagram_orange,
        });

    const availableTabs = [];
    if (timeSeriesGoogle)
        availableTabs.push({ value: 'google', label: 'Google' });
    if (timeSeriesTikTok)
        availableTabs.push({ value: 'tiktok', label: 'TikTok' });
    if (
        timeSeriesInstagram &&
        country &&
        INSTAGRAM_COUNTRIES.includes(country.text_id)
    )
        availableTabs.push({ value: 'instagram', label: 'Instagram' });
    const [activeTab, setActiveTab] = useState(availableTabs[0]?.value);

    if (!paginateItem) {
        return <Message>Item not found.</Message>;
    }

    const seeMoreHref = getPathFromNode(
        {
            id: paginateBy === 'brands' ? brand : category,
            type: paginateType,
            depth: paginateBy === 'brands' ? null : 'cat5',
        },
        parentId,
    );

    return (
        <Stack $space="1.5rem" css="padding-bottom: 36px;">
            <DrawerHeader
                name={name}
                eyebrow={
                    <>
                        <DimensionIcon dimensionType={paginateType} />
                        {paginateType}
                    </>
                }
                contextLabel={subcategory}
            >
                {showAddToWatchlist && (
                    <ToggleWatch
                        id={paginateType === 'category' ? category : brand}
                        data={{
                            name,
                            type:
                                paginateType === 'category'
                                    ? 'category'
                                    : 'brand',
                        }}
                        btnChildren="Add to watchlist"
                    />
                )}
                <Button variant="default" size="sm" asChild>
                    <LinkTracked
                        to={seeMoreHref}
                        mixpanelContext="DataTableDrawer"
                        mixpanelMeta={{ itemNames: [name] }}
                    >
                        See more data
                        <FiExternalLink />
                    </LinkTracked>
                </Button>
            </DrawerHeader>
            <DrawerBand>
                <Card className="overflow-visible">
                    {data && (
                        <div className="px-3 py-2 border-b border-border">
                            <LifeCycleRow data={data} />
                        </div>
                    )}
                    <MetricHeroCard
                        source={DATA_SOURCES.POPULARITY}
                        data={data}
                        healthStats={data?.healthStats}
                        cluster={data?.cluster}
                        layout="wrap"
                    />
                </Card>
                <Card>
                    <CardContent className="p-4 flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <div className="flex gap-4">
                                <div className="flex items-center gap-1.5">
                                    <span
                                        className="w-0.5 h-4 rounded-full"
                                        style={{
                                            backgroundColor: theme.google_blue,
                                        }}
                                    />
                                    <span className="text-xs font-semibold uppercase">
                                        {POPULARITY_METADATA_BY_KEY.search_YoY.format(
                                            data?.[
                                                POPULARITY_METADATA_BY_KEY
                                                    .search_YoY.key
                                            ],
                                        ) || '—'}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                        Google
                                    </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span
                                        className="w-0.5 h-4 rounded-full"
                                        style={{
                                            backgroundColor: theme.tiktok_pink,
                                        }}
                                    />
                                    <span className="text-xs font-semibold uppercase">
                                        {POPULARITY_METADATA_BY_KEY.tiktok_YoY.format(
                                            data?.[
                                                POPULARITY_METADATA_BY_KEY
                                                    .tiktok_YoY.key
                                            ],
                                        ) || '—'}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                        TikTok
                                    </span>
                                </div>
                                {country &&
                                    INSTAGRAM_COUNTRIES.includes(
                                        country.text_id,
                                    ) && (
                                        <div className="flex items-center gap-1.5">
                                            <span
                                                className="w-0.5 h-4 rounded-full"
                                                style={{
                                                    backgroundColor:
                                                        theme.instagram_orange,
                                                }}
                                            />
                                            <span className="text-xs font-semibold uppercase">
                                                {POPULARITY_METADATA_BY_KEY.instagram_YoY.format(
                                                    data?.[
                                                        POPULARITY_METADATA_BY_KEY
                                                            .instagram_YoY.key
                                                    ],
                                                ) || '—'}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                                Instagram
                                            </span>
                                        </div>
                                    )}
                            </div>
                            <DownloadPopularityTimeSeriesButton
                                menuAlign="right"
                                csvFilename={buildExportFilename({
                                    entity: name,
                                    country,
                                    source: 'pop-index',
                                    kind: 'time-series',
                                    date: popIndexDate,
                                })}
                                imageTitle={timeSeriesName}
                                data={tsData}
                                wrapperRef={timeSeriesRef}
                            />
                        </div>
                        <ErrorBoundary>
                            <div ref={timeSeriesRef}>
                                <PopularityTimeSeries
                                    data={tsData}
                                    showPlaceholder={tsData.length < 1}
                                    children={null}
                                    chartHeight={CHART_HEIGHT}
                                    name={name}
                                    showKey={false}
                                />
                            </div>
                        </ErrorBoundary>
                    </CardContent>
                </Card>
                {paginateType === 'category' && (
                    <CategoryDescriptionCard
                        categoryId={category}
                        categoryName={name}
                        flavors={paginateItem?.flavors}
                    />
                )}
            </DrawerBand>
            {availableTabs.length > 0 && (
                <DrawerStack css="padding-bottom: 2rem;">
                    <div css="display: flex; justify-content: center;">
                        <div className="flex items-center gap-1 rounded-xl bg-muted p-1">
                            {availableTabs.map(({ value, label }) => (
                                <button
                                    key={value}
                                    type="button"
                                    className={`px-3 py-1.5 text-sm rounded-lg ${
                                        activeTab === value
                                            ? 'bg-card shadow-sm text-primary'
                                            : 'text-foreground hover:bg-muted-foreground/10'
                                    }`}
                                    onClick={() => setActiveTab(value)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div style={{ minHeight: '20rem' }}>
                        {activeTab === 'google' && timeSeriesGoogle && (
                            <TopKeywordsDrawerSection
                                enabled={!!((brand || category) && country)}
                                data={{
                                    countries: country ? [country.id] : null,
                                    brands: brand ? [brand] : null,
                                    categories: category ? [category] : null,
                                }}
                                paginateBy={paginateBy}
                                name={name}
                                filename={buildExportFilename({
                                    entity: name,
                                    country,
                                    source: 'google',
                                    type: 'search-query',
                                    kind: 'data',
                                    date: googleDate,
                                })}
                                displayQty={10}
                                showIcon={false}
                            />
                        )}

                        {activeTab === 'tiktok' && timeSeriesTikTok && (
                            <TopHashtagsDrawerSection
                                enabled={
                                    !!(
                                        (brand || category) &&
                                        country &&
                                        week &&
                                        year
                                    )
                                }
                                data={{
                                    countries: country ? [country.id] : null,
                                    brands: brand ? [brand] : null,
                                    categories: category ? [category] : null,
                                    week,
                                    year,
                                }}
                                filename={buildExportFilename({
                                    entity: name,
                                    country,
                                    source: 'tiktok',
                                    type: 'hashtag',
                                    kind: 'data',
                                    date: tiktokDate,
                                })}
                                displayQty={10}
                                showIcon={false}
                                {...(weeksQuery?.isLoading
                                    ? { isLoading: true }
                                    : {})}
                            />
                        )}

                        {activeTab === 'instagram' &&
                            timeSeriesInstagram &&
                            country &&
                            INSTAGRAM_COUNTRIES.includes(country.text_id) && (
                                <TopInstagramHashtagsDrawerSection
                                    enabled={
                                        !!(
                                            (brand || category) &&
                                            country &&
                                            week &&
                                            year
                                        )
                                    }
                                    data={{
                                        countries: country
                                            ? [country.id]
                                            : null,
                                        brands: brand ? [brand] : null,
                                        categories: category
                                            ? [category]
                                            : null,
                                        week,
                                        year,
                                    }}
                                    filename={buildExportFilename({
                                        entity: name,
                                        country,
                                        source: 'instagram',
                                        type: 'hashtag',
                                        kind: 'data',
                                        date: instagramDate,
                                    })}
                                    displayQty={10}
                                    showIcon={false}
                                    {...(weeksQuery?.isLoading
                                        ? { isLoading: true }
                                        : {})}
                                />
                            )}
                    </div>
                </DrawerStack>
            )}
        </Stack>
    );
}

export default OverviewDrawer;
